/**
 * Hook for subscription form handlers and mutations.
 * Manages form navigation, validation, and submission.
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  SubmitHandler,
  UseFormHandleSubmit,
  UseFormSetError,
  UseFormTrigger,
  UseFormWatch,
  UseFormSetValue,
} from "react-hook-form";
import { toast } from "@altimateai/lego";
import { SubscriptionFormData, SubscriptionV2Variable } from "@/pages/subscriptions/services/types";
import { handleEmailDomainSaveError } from "@/helpers/handleSaveError";
import { useCreateSubscriptionV2, useUpdateSubscriptionV2 } from "../services/wizardApi";
import type { MetricDefinition } from "@/pages/subscriptions/services/types";
import { generatePromptFromFormData } from "../utils/subscriptionPromptGenerator";
import { resolvePlatformWithMetricFallback } from "../utils/subscriptionTransformers";
import { SubscriptionType, VariableType, WizardStepEnum } from "@/pages/subscriptions/constants";
import { AlertsTracking } from "@/lib/tracking/alerts";
import type { WizardStep } from "../types";

/**
 * Determines if prompt/variables need to be regenerated based on form changes.
 * Checks for changes in subscription type, source, metric, and filters.
 */
function shouldRegeneratePrompt(
  currentVariables: SubscriptionV2Variable[],
  formData: SubscriptionFormData
): boolean {
  // No variables means we need to generate
  if (currentVariables.length === 0) {
    return true;
  }

  // Check if subscription type changed
  const isCurrentlyAlert = currentVariables.some(v => v.type === VariableType.CHANGE_TYPE);
  const shouldBeAlert = formData.rule_type === SubscriptionType.ALERT;
  if (isCurrentlyAlert !== shouldBeAlert) {
    return true;
  }

  // Check if source changed
  const entityVar = currentVariables.find(v => v.type === VariableType.ENTITY);
  const expectedEntityValue = `all_${formData.source}s`;
  if (!entityVar || entityVar.value !== expectedEntityValue) {
    return true;
  }

  // Check if metric changed
  const metricVar = currentVariables.find(v => v.type === VariableType.METRIC);
  if (!metricVar || metricVar.value !== formData.metric) {
    return true;
  }

  // Check if filters changed
  const currentFilterCount = entityVar?.filterCount || 0;
  const newFilterCount = formData.filters?.length || 0;
  if (currentFilterCount !== newFilterCount) {
    return true;
  }

  return false;
}

interface UseSubscriptionFormHandlersProps {
  subscriptionId?: string;
  isEditMode: boolean;
  metricsData?: MetricDefinition[];
  trigger: UseFormTrigger<SubscriptionFormData>;
  watch: UseFormWatch<SubscriptionFormData>;
  setValue: UseFormSetValue<SubscriptionFormData>;
  setError: UseFormSetError<SubscriptionFormData>;
  handleSubmit: UseFormHandleSubmit<SubscriptionFormData>;
  setCurrentStep: (step: WizardStep) => void;
}

interface UseSubscriptionFormHandlersReturn {
  subscriptionMutation:
    | ReturnType<typeof useCreateSubscriptionV2>
    | ReturnType<typeof useUpdateSubscriptionV2>;
  onSubmit: SubmitHandler<SubscriptionFormData>;
  handleBack: () => void;
  handleClose: () => void;
  handleSubscriptionTypeNext: () => Promise<void>;
  handleCustomizeNext: () => void;
  handleDestinationSubmit: () => Promise<void>;
  handleGoToStep: (step: WizardStep) => void;
}

export const useSubscriptionFormHandlers = ({
  subscriptionId,
  isEditMode,
  metricsData,
  trigger,
  watch,
  setValue,
  setError,
  handleSubmit,
  setCurrentStep,
}: UseSubscriptionFormHandlersProps): UseSubscriptionFormHandlersReturn => {
  const navigate = useNavigate();

  // Initialize mutations
  const createSubscriptionMutation = useCreateSubscriptionV2();
  const updateSubscriptionMutation = useUpdateSubscriptionV2();

  // Use the appropriate mutation based on edit mode
  const subscriptionMutation = isEditMode ? updateSubscriptionMutation : createSubscriptionMutation;

  const onSubmit: SubmitHandler<SubscriptionFormData> = useCallback(
    async data => {
      const resolvedPlatform = resolvePlatformWithMetricFallback(data, metricsData);
      if (!resolvedPlatform) {
        const message =
          "Cannot determine platform: missing source_name on the selected metric. " +
          "Pick a category whose source is Snowflake or Databricks before saving.";
        AlertsTracking.saveFailed(message);
        toast({
          variant: "destructive",
          title: `Failed to ${isEditMode ? "update" : "create"} subscription`,
          description: message,
        });
        return;
      }
      data.platform = resolvedPlatform;

      AlertsTracking.createSubmitted(data.rule_type);
      try {
        if (isEditMode && subscriptionId) {
          await updateSubscriptionMutation.mutateAsync({ id: subscriptionId, formData: data });
          AlertsTracking.ruleUpdated(subscriptionId);
        } else {
          await createSubscriptionMutation.mutateAsync(data);
          AlertsTracking.ruleCreated(data.rule_type);
        }
        navigate("/subscriptions");
      } catch (error) {
        handleEmailDomainSaveError(
          setError,
          "email_recipients",
          error,
          `Failed to ${isEditMode ? "update" : "create"} subscription`,
          AlertsTracking.saveFailed
        );
      }
    },
    [
      isEditMode,
      subscriptionId,
      navigate,
      createSubscriptionMutation,
      updateSubscriptionMutation,
      metricsData,
      setError,
    ]
  );

  const handleBack = useCallback(() => {
    if (isEditMode && subscriptionId) {
      navigate(`/subscriptions/${subscriptionId}`);
    } else {
      navigate("/subscriptions");
    }
  }, [navigate, isEditMode, subscriptionId]);

  const handleClose = useCallback(() => {
    if (isEditMode && subscriptionId) {
      navigate(`/subscriptions/${subscriptionId}`);
    } else {
      navigate("/subscriptions");
    }
  }, [navigate, isEditMode, subscriptionId]);

  const handleSubscriptionTypeNext = useCallback(async () => {
    const isValid = await trigger(["name", "rule_type", "source", "metric", "filters"]);
    if (isValid) {
      const formData = watch();
      const currentVariables = formData.variables || [];

      AlertsTracking.ruleTypeSelected(formData.rule_type);
      if (formData.source) AlertsTracking.entityTypeSelected(formData.source);
      if (formData.metric) AlertsTracking.metricSelected(formData.metric, formData.source || "");
      AlertsTracking.wizardStepChanged(0, 1);

      // Use helper function to determine if regeneration is needed
      const shouldRegenerate = shouldRegeneratePrompt(currentVariables, formData);

      if (shouldRegenerate) {
        const metricDefinition = metricsData?.find(m => m.name === formData.metric);

        const { prompt, variables } = generatePromptFromFormData(
          {
            rule_type: formData.rule_type,
            source: formData.source,
            metric: formData.metric,
            filters: formData.filters,
          },
          metricDefinition
            ? {
                default_aggregation: metricDefinition.default_aggregation,
                default_comparison_type: metricDefinition.default_comparison_type,
                default_threshold_type: metricDefinition.default_threshold_type,
                default_threshold: metricDefinition.default_threshold,
                default_date_range: metricDefinition.default_date_range,
                metric_display_name: metricDefinition.display_name,
                category_display_name: metricDefinition.category_display_name,
              }
            : undefined
        );

        setValue("prompt", prompt);
        setValue("variables", variables);
      }
      setCurrentStep(WizardStepEnum.CUSTOMIZE);
    }
  }, [trigger, watch, setValue, metricsData, setCurrentStep]);

  const handleGoToStep = useCallback(
    (step: WizardStep) => {
      setCurrentStep(step);
    },
    [setCurrentStep]
  );

  const handleCustomizeNext = useCallback(() => {
    AlertsTracking.wizardStepChanged(1, 2);
    setCurrentStep(WizardStepEnum.DESTINATION);
  }, [setCurrentStep]);

  const handleDestinationSubmit = useCallback(async () => {
    const isValid = await trigger(["channels", "email_recipients", "slack_channels"]);
    if (isValid) {
      const formData = watch();
      AlertsTracking.notifierConfigured(formData.channels || []);
      await handleSubmit(onSubmit)();
    }
  }, [trigger, watch, handleSubmit, onSubmit]);

  return {
    subscriptionMutation,
    onSubmit,
    handleBack,
    handleClose,
    handleSubscriptionTypeNext,
    handleCustomizeNext,
    handleDestinationSubmit,
    handleGoToStep,
  };
};
